import { useState, useEffect } from 'react';
import { Bell, Plus, Trash2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { EmptyState } from '@/components/common/EmptyState';
import { SkeletonCard } from '@/components/common/SkeletonCard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { listAlerts, createAlert, updateAlert, deleteAlert } from '@/services/authClient';

const EVENT_TYPES = [
  { value: 'new_listing', label: 'New listings' },
  { value: 'price_drop', label: 'Price drops' },
  { value: 'status_change', label: 'Status changes' },
];

const CHANNELS = [
  { value: 'in_app', label: 'In-app notifications' },
  { value: 'email', label: 'Email notifications' },
];

const FREQUENCIES = [
  { value: 'instant', label: 'Instant' },
  { value: 'daily', label: 'Daily digest' },
  { value: 'weekly', label: 'Weekly digest' },
];

export default function Alerts() {
  const [alerts, setAlerts] = useState(null);
  const [open, setOpen] = useState(false);
  const [limitError, setLimitError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    event_types: ['new_listing'],
    channels: ['in_app'],
    frequency: 'instant',
    filters: {},
  });

  useEffect(() => {
    listAlerts()
      .then((data) => setAlerts(data.results || data || []))
      .catch(() => setAlerts([]));
  }, []);

  const handleToggle = async (id, isActive) => {
    try {
      await updateAlert(id, { is_active: !isActive });
      setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, is_active: !isActive } : a)));
      toast(isActive ? 'Alert paused' : 'Alert activated');
    } catch {
      toast.error('Could not update alert');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAlert(id);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
      toast('Alert deleted');
    } catch {
      toast.error('Could not delete alert');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLimitError(null);
    try {
      const payload = {
        name: formData.name,
        filters: formData.filters,
        event_types: formData.event_types,
        channels: formData.channels,
        frequency: formData.frequency,
        is_active: true,
      };
      const newAlert = await createAlert(payload);
      setAlerts((prev) => [...prev, newAlert]);
      toast('Alert created');
      setOpen(false);
      setFormData({ name: '', event_types: ['new_listing'], channels: ['in_app'], frequency: 'instant', filters: {} });
    } catch (err) {
      if (err.response?.status === 403) {
        setLimitError(err.response?.data?.detail || 'Alert limit reached');
      } else {
        toast.error('Could not create alert');
      }
    }
  };

  const toggleEventType = (value) => {
    setFormData((prev) => ({
      ...prev,
      event_types: prev.event_types.includes(value)
        ? prev.event_types.filter((v) => v !== value)
        : [...prev.event_types, value],
    }));
  };

  const toggleChannel = (value) => {
    setFormData((prev) => ({
      ...prev,
      channels: prev.channels.includes(value)
        ? prev.channels.filter((v) => v !== value)
        : [...prev.channels, value],
    }));
  };

  if (alerts === null) {
    return (
      <div>
        <h1 className="font-display text-2xl font-semibold text-slate-900">Alerts</h1>
        <p className="text-sm text-slate-600">Manage your saved search alerts.</p>
        <div className="mt-6 space-y-3">
          {[1, 2].map((i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">Alerts</h1>
          <p className="text-sm text-slate-600">Get notified when new homes match your criteria.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl">
              <Plus size={16} className="mr-1" />
              Create alert
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create new alert</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              {limitError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {limitError}{' '}
                    <a href="/dashboard/billing" className="underline font-medium">
                      Upgrade your plan
                    </a>{' '}
                    to create more alerts.
                  </AlertDescription>
                </Alert>
              )}
              <div>
                <Label>Alert name</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Downtown condos under $500k"
                  className="mt-1.5 rounded-xl"
                />
              </div>
              <div>
                <Label>Notify me about</Label>
                <div className="mt-2 space-y-2">
                  {EVENT_TYPES.map((t) => (
                    <label key={t.value} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={formData.event_types.includes(t.value)}
                        onCheckedChange={() => toggleEventType(t.value)}
                      />
                      {t.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Send notifications via</Label>
                <div className="mt-2 space-y-2">
                  {CHANNELS.map((c) => (
                    <label key={c.value} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={formData.channels.includes(c.value)}
                        onCheckedChange={() => toggleChannel(c.value)}
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Frequency</Label>
                <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                  <SelectTrigger className="mt-1.5 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1 rounded-xl">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-xl">
                  Create
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="mt-6">
        {alerts.length === 0 ? (
          <EmptyState
            icon={Bell}
            title="No alerts yet"
            description="Create an alert to get notified when new listings match your search criteria."
            actionLabel="Create alert"
            onAction={() => setOpen(true)}
          />
        ) : (
          <div className="space-y-3">
            {alerts.map((a) => (
              <div
                key={a.id}
                className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                data-testid="alert-item"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900">{a.name}</p>
                    {!a.is_active && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">Paused</span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5 text-xs text-slate-600">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5">
                      {a.event_types?.join(', ').replace(/_/g, ' ')}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5">{a.frequency}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5">
                      {a.channels?.join(', ').replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={a.is_active} onCheckedChange={() => handleToggle(a.id, a.is_active)} />
                  <button
                    onClick={() => handleDelete(a.id)}
                    className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
                    aria-label="Delete"
                    data-testid="alert-delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
